import type {ReactElement} from 'react';
import type {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {PlanTableComponentPart} from '@Components/ai-chat-window/components/plan-table/plan-table.types';
import type {ButtonGroupComponentPart} from '@Components/ai-chat-window/components/button-group/button-group.types';
import type {EntityDraftCardComponentPart} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card.types';
import type {EntityDraftSkeletonComponentPart} from '@Components/ai-chat-window/components/entity-draft-skeleton/entity-draft-skeleton.types';
import type {EntityCardComponentPart} from '@Components/ai-chat-window/components/entity-card/entity-card.types';
import type {EntityCardSkeletonComponentPart} from '@Components/ai-chat-window/components/entity-card-skeleton/entity-card-skeleton.types';

export enum StreamComponentType {
  PLAN_TABLE = 'plan_table',
  BUTTON_GROUP = 'button_group',
  BUTTON_GROUP_SKELETON = 'button_group_skeleton',
  SURVEY = 'survey',
  ENTITY_DRAFT = 'entity_draft',
  ENTITY_DRAFT_SKELETON = 'entity_draft_skeleton',
  ENTITY_CARD = 'entity_card',
  ENTITY_CARD_SKELETON = 'entity_card_skeleton',
}

export interface StreamComponentContext {
  chatId: string;
}

export interface StreamComponentRegistration<TComponentType extends StreamComponentType, TProps> {
  componentType: TComponentType;
  /** Validates the LLM-authored payload; returns null for a malformed shape so the part is dropped instead of crashing at render. */
  parseProps: (props: Record<string, unknown>) => TProps | null;
  render: (props: TProps, context: StreamComponentContext) => ReactElement;
}

/**
 * A props-type-erased registration for the registry. parse-then-render happen at
 * different times (parse on stream receipt, render later from the stored part), so
 * unlike the entity-card action they can't be fused; instead defineStreamComponent
 * closes over TProps and re-asserts it at the render boundary the parse already validated.
 */
export interface RegisteredStreamComponent {
  componentType: StreamComponentType;
  /** Widened from the typed registration's `TProps | null`; still returns null for a malformed shape. */
  parseProps: (props: Record<string, unknown>) => unknown;
  render: (props: unknown, context: StreamComponentContext) => ReactElement;
}

export interface BaseComponentPart<TComponentType extends StreamComponentType, TProps> {
  type: ChatMessagePartType.COMPONENT;
  componentType: TComponentType;
  props: TProps;
  isNextSuggestion?: boolean;
}

export type ChatMessageComponentPart = PlanTableComponentPart | ButtonGroupComponentPart | EntityDraftCardComponentPart | EntityDraftSkeletonComponentPart | EntityCardComponentPart | EntityCardSkeletonComponentPart;
